// src/components/FindPeopleDialog.js

import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, Button, Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, CircularProgress } from '@mui/material';
import { db, auth } from '../config/firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';

const FindPeopleDialog = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [requestSent, setRequestSent] = useState({});

  const handleSearch = async () => {
    if (searchTerm.trim() === '') return;
    setLoading(true);
    setMessage('');
    setSearchResults([]);
    setRequestSent({});

    const currentUser = auth.currentUser;
    if (searchTerm.toLowerCase() === currentUser.email.toLowerCase()) {
      setMessage("You can't search for yourself.");
      setLoading(false);
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where("email", "==", searchTerm.toLowerCase()));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setMessage("No user found with that email.");
      } else {
        const users = [];
        querySnapshot.forEach((doc) => {
          users.push({ id: doc.id, ...doc.data() });
        });
        setSearchResults(users);
      }
    } catch (error) {
      console.error("Error searching for user: ", error);
      setMessage("An error occurred. Please check the console.");
    }
    setLoading(false);
  };

  const handleSendRequest = async (receiverId) => {
    const senderId = auth.currentUser.uid;
    try {
      await addDoc(collection(db, 'connection_requests'), {
        senderId: senderId,
        receiverId: receiverId,
        status: 'pending',
        createdAt: serverTimestamp()
      });
      setRequestSent(prev => ({ ...prev, [receiverId]: true }));
    } catch (error) {
      console.error("Error sending connection request: ", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Find People</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Find other users by their exact email address.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button onClick={handleSearch} variant="contained" sx={{ mt: '8px', mb: '4px', height: '40px' }}>
            Search
          </Button>
        </Box>

        <Box id="search-results" sx={{ mt: 2, minHeight: '100px' }}>
          {loading && <CircularProgress size={24} />}
          {!loading && message && <Typography color="textSecondary">{message}</Typography>}
          {!loading && searchResults.length > 0 && (
            <List>
              {searchResults.map((user) => (
                <ListItem key={user.id} secondaryAction={
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleSendRequest(user.id)}
                    disabled={requestSent[user.id]}
                  >
                    {requestSent[user.id] ? 'Sent' : 'Connect'}
                  </Button>
                }>
                  <ListItemAvatar>
                    <Avatar src={user.photoURL} />
                  </ListItemAvatar>
                  <ListItemText primary={user.displayName} secondary={user.email} />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FindPeopleDialog;